import React, { useState, useEffect, useMemo } from "react";
import { useActiveAccount, useActiveWallet } from "thirdweb/react";
import { ThirdwebWalletConnect } from "../components/ThirdwebWalletConnect";
// ABI will be loaded dynamically
import { logger } from "../utils/logger";
import { useHttp } from "../hooks/useHttp";
import { useNavigate } from "react-router-dom";
import { ethers } from "ethers";
import { getEventsRecursive } from "../utils/ethEvents";
import { getMewToyImageUrl } from "../utils/mew";

interface NFT {
  tokenId: string;
  name: string;
  image: string;
  uid?: string;
  toyType?: number;
  tokenType?: number;
  secretLink?: string;
}

export function ClaimAdditional() {
  const navigate = useNavigate();
  const wallet = useActiveWallet();

  const account = useActiveAccount();
  const address = account?.address;
  const eoaAccount = useMemo(() => {
    return (
      (wallet as any)?.getAdminAccount?.() ||
      (wallet as any)?.getAccount?.() ||
      account
    );
  }, [wallet, account]);
  const eoaAddress = eoaAccount?.address;
  const { sendRequest } = useHttp();

  const [isSigning, setIsSigning] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nfts, setNfts] = useState<NFT[]>([]);
  const [revealedLinks, setRevealedLinks] = useState<Record<string, string>>(
    {}
  );

  const [showAllNFTs, setShowAllNFTs] = useState(false);

  useEffect(() => {
    console.log(`ClaimAdditional page loaded`, { 
      hasAddress: !!address,
      hasEoaAddress: !!eoaAddress,
    });

    // Load NFTs when we have at least one address (eoaAddress for Chimpers, address for Ponke)
    if (address || eoaAddress) {
      logger.contract(`Loading user Digital Collectibles for addresses`, {
        address: address,
        eoaAddress: eoaAddress,
      });
      loadUserNFTs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address, eoaAddress]);

  // Load Chimpers NFTs (Avalanche chain)
  const loadChimpersNFTs = async (): Promise<NFT[]> => {
    if (!eoaAddress) return [];

    const contractAddress = process.env.REACT_APP_CONTRACT_ADDRESS;
    if (!contractAddress) {
      logger.contractError(
        "Chimpers contract address is not configured",
        new Error("Missing REACT_APP_CONTRACT_ADDRESS")
      );
      return [];
    }

    try {
      // Load ABI dynamically from public directory
      const response = await fetch("/NFC223Abi.json");
      const NFC223Abi = await response.json();

      // Create a read-only provider for the active chain
      const rpcUrl = process.env.REACT_APP_RPC_URL;
      const provider = new ethers.providers.JsonRpcProvider(rpcUrl);

      const contract = new ethers.Contract(
        contractAddress,
        NFC223Abi,
        provider
      );

      // Get balance from smart contract using eoaAddress
      const balance = await contract.balanceOf(eoaAddress);
      const nftCount = Number(balance);
      logger.contract(`Chimpers Digital Collectible balance retrieved`, {
        eoaAddress,
        balance: nftCount,
      });

      if (nftCount === 0) {
        return [];
      }

      // Read Transfer events using recursive paged query
      const addressLower = eoaAddress.toLowerCase();
      const START_BLOCK_ENV = process.env.REACT_APP_CONTRACT_DEPLOY_BLOCK;
      if (!START_BLOCK_ENV) {
        throw new Error("REACT_APP_CONTRACT_DEPLOY_BLOCK is not configured");
      }
      const START_BLOCK = parseInt(START_BLOCK_ENV, 10);

      const ethContract = new ethers.Contract(
        contractAddress,
        NFC223Abi,
        provider
      );
      const [toEvents, fromEvents] = await Promise.all([
        getEventsRecursive({
          contract: ethContract,
          eventName: "Transfer",
          args: [null, eoaAddress],
          fromBlock: START_BLOCK,
        }),
        getEventsRecursive({
          contract: ethContract,
          eventName: "Transfer",
          args: [eoaAddress, null],
          fromBlock: START_BLOCK,
        }),
      ]);

      const combined = [...toEvents, ...fromEvents];
      combined.sort((a, b) => {
        if (a.blockNumber !== b.blockNumber)
          return b.blockNumber - a.blockNumber;
        return (b.logIndex ?? 0) - (a.logIndex ?? 0);
      });

      const ownedTokenIds: string[] = [];
      const seenTokenIds = new Set<string>();
      for (const ev of combined) {
        const args: any = ev.args;
        if (!args) continue;
        const tokenIdStr = args.tokenId?.toString?.();
        if (!tokenIdStr || seenTokenIds.has(tokenIdStr)) continue;
        seenTokenIds.add(tokenIdStr);
        const to = String(args.to).toLowerCase();
        if (to === addressLower) {
          ownedTokenIds.push(tokenIdStr);
          if (ownedTokenIds.length >= nftCount) break;
        }
      }

      const userNFTs: NFT[] = [];
      for (const tokenIdStr of ownedTokenIds) {
        const toyType = await contract.tokenIdToToyType(tokenIdStr);
        userNFTs.push({
          tokenId: tokenIdStr,
          name: `223 NFC #${tokenIdStr}`,
          image: `https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=300&h=300&fit=crop&crop=center&sig=${tokenIdStr}`,
          toyType: Number(toyType),
        });
      }

      logger.contract(`Chimpers NFTs loaded`, {
        totalFound: userNFTs.length,
        expected: nftCount,
      });

      return userNFTs;
    } catch (err) {
      logger.contractError("Failed to load Chimpers NFTs", err as Error, {
        eoaAddress,
        contractAddress: process.env.REACT_APP_CONTRACT_ADDRESS,
      });
      return [];
    }
  };

  // Load Ponke NFTs (Base chain)
  const loadPonkeNFTs = async (): Promise<NFT[]> => {
    // Need either address or eoaAddress to load NFTs
    if (!address && !eoaAddress) return [];

    const contractAddress = process.env.REACT_APP_PONKE_CONTRACT_ADDRESS;
    if (!contractAddress) {
      logger.contractError(
        "Ponke contract address is not configured",
        new Error("Missing REACT_APP_PONKE_CONTRACT_ADDRESS")
      );
      return [];
    }

    try {
      // Load ABI dynamically from public directory
      const response = await fetch("/PonkeNftAbi.json");
      const PonkeNftAbi = await response.json();

      // Create a read-only provider for the Ponke chain
      const rpcUrl = process.env.REACT_APP_PONKE_RPC_URL;
      const provider = new ethers.providers.JsonRpcProvider(rpcUrl);

      const contract = new ethers.Contract(
        contractAddress,
        PonkeNftAbi,
        provider
      );

      // Get balance from smart contract - use eoaAddress since NFTs are minted to EOA
      const balanceAddress = eoaAddress || address;
      const balance = await contract.balanceOf(balanceAddress);
      const nftCount = Number(balance);
      logger.contract(`Ponke Digital Collectible balance retrieved`, {
        address: balanceAddress,
        balance: nftCount,
      });

      if (nftCount === 0) {
        return [];
      }

      // Read Transfer events using recursive paged query
      // Use same address for event filtering as balance check (eoaAddress)
      const addressLower = balanceAddress.toLowerCase();
      const START_BLOCK_ENV = process.env.REACT_APP_PONKE_CONTRACT_DEPLOY_BLOCK;
      if (!START_BLOCK_ENV) {
        throw new Error("REACT_APP_PONKE_CONTRACT_DEPLOY_BLOCK is not configured");
      }
      const START_BLOCK = parseInt(START_BLOCK_ENV, 10);

      const ethContract = new ethers.Contract(
        contractAddress,
        PonkeNftAbi,
        provider
      );
      const [toEvents, fromEvents] = await Promise.all([
        getEventsRecursive({
          contract: ethContract,
          eventName: "Transfer",
          args: [null, balanceAddress],
          fromBlock: START_BLOCK,
        }),
        getEventsRecursive({
          contract: ethContract,
          eventName: "Transfer",
          args: [balanceAddress, null],
          fromBlock: START_BLOCK,
        }),
      ]);

      const combined = [...toEvents, ...fromEvents];
      combined.sort((a, b) => {
        if (a.blockNumber !== b.blockNumber)
          return b.blockNumber - a.blockNumber;
        return (b.logIndex ?? 0) - (a.logIndex ?? 0);
      });

      const ownedTokenIds: string[] = [];
      const seenTokenIds = new Set<string>();
      for (const ev of combined) {
        const args: any = ev.args;
        if (!args) continue;
        const tokenIdStr = args.tokenId?.toString?.();
        if (!tokenIdStr || seenTokenIds.has(tokenIdStr)) continue;
        seenTokenIds.add(tokenIdStr);
        const to = String(args.to).toLowerCase();
        if (to === addressLower) {
          ownedTokenIds.push(tokenIdStr);
          if (ownedTokenIds.length >= nftCount) break;
        }
      }

      const userNFTs: NFT[] = [];
      for (const tokenIdStr of ownedTokenIds) {
        // Ponke NFTs don't have toyType - all are the same type
        userNFTs.push({
          tokenId: tokenIdStr,
          name: `Ponke & The Combat Cone™ #${tokenIdStr}`,
          // All Ponke NFTs use the same image
          image: `/images/ponke/1.png`,
        });
      }

      logger.contract(`Ponke NFTs loaded`, {
        totalFound: userNFTs.length,
        expected: nftCount,
      });

      return userNFTs;
    } catch (err) {
      logger.contractError("Failed to load Ponke NFTs", err as Error, {
        address,
        contractAddress: process.env.REACT_APP_PONKE_CONTRACT_ADDRESS,
      });
      return [];
    }
  };

  // Load Mew NFTs (Avalanche chain)
  const loadMewNFTs = async (): Promise<NFT[]> => {
    // Need either address or eoaAddress to load NFTs
    if (!address && !eoaAddress) return [];

    const contractAddress = process.env.REACT_APP_MEW_CONTRACT_ADDRESS;
    if (!contractAddress) {
      logger.contractError(
        "Mew contract address is not configured",
        new Error("Missing REACT_APP_MEW_CONTRACT_ADDRESS")
      );
      return [];
    }

    try {
      // Load ABI dynamically from public directory
      const response = await fetch("/MewNftAbi.json");
      const MewNftAbi = await response.json();

      // Create a read-only provider for the Mew chain
      const rpcUrl = process.env.REACT_APP_MEW_RPC_URL;
      const provider = new ethers.providers.JsonRpcProvider(rpcUrl);

      const contract = new ethers.Contract(
        contractAddress,
        MewNftAbi,
        provider
      );

      // Get balance from smart contract - use eoaAddress since NFTs are minted to EOA
      const balanceAddress = eoaAddress || address;
      const balance = await contract.balanceOf(balanceAddress);
      const nftCount = Number(balance);
      logger.contract(`Mew Digital Collectible balance retrieved`, {
        address: balanceAddress,
        balance: nftCount,
      });

      if (nftCount === 0) {
        return [];
      }

      // Read Transfer events using recursive paged query
      // Use same address for event filtering as balance check (eoaAddress)
      const addressLower = balanceAddress.toLowerCase();
      const START_BLOCK_ENV = process.env.REACT_APP_MEW_CONTRACT_DEPLOY_BLOCK;
      if (!START_BLOCK_ENV) {
        throw new Error("REACT_APP_MEW_CONTRACT_DEPLOY_BLOCK is not configured");
      }
      const START_BLOCK = parseInt(START_BLOCK_ENV, 10);

      const ethContract = new ethers.Contract(
        contractAddress,
        MewNftAbi,
        provider
      );
      const [toEvents, fromEvents] = await Promise.all([
        getEventsRecursive({
          contract: ethContract,
          eventName: "Transfer",
          args: [null, balanceAddress],
          fromBlock: START_BLOCK,
        }),
        getEventsRecursive({
          contract: ethContract,
          eventName: "Transfer",
          args: [balanceAddress, null],
          fromBlock: START_BLOCK,
        }),
      ]);

      const combined = [...toEvents, ...fromEvents];
      combined.sort((a, b) => {
        if (a.blockNumber !== b.blockNumber)
          return b.blockNumber - a.blockNumber;
        return (b.logIndex ?? 0) - (a.logIndex ?? 0);
      });

      const ownedTokenIds: string[] = [];
      const seenTokenIds = new Set<string>();
      for (const ev of combined) {
        const args: any = ev.args;
        if (!args) continue;
        const tokenIdStr = args.tokenId?.toString?.();
        if (!tokenIdStr || seenTokenIds.has(tokenIdStr)) continue;
        seenTokenIds.add(tokenIdStr);
        const to = String(args.to).toLowerCase();
        if (to === addressLower) {
          ownedTokenIds.push(tokenIdStr);
          if (ownedTokenIds.length >= nftCount) break;
        }
      }

      const userNFTs: NFT[] = [];
      for (const tokenIdStr of ownedTokenIds) {
        try {
          const tokenType = await contract.tokenType(tokenIdStr);
          const tokenTypeNum = Number(tokenType);
          userNFTs.push({
            tokenId: tokenIdStr,
            name: `Catch Mew If You Can #${tokenIdStr.padStart(3, '0')}`,
            tokenType: tokenTypeNum,
            image: getMewToyImageUrl(tokenTypeNum),
          });
        } catch (err) {
          logger.contractError(
            `Failed to get tokenType for tokenId ${tokenIdStr}`,
            err as Error
          );
          // Fallback if tokenType fails
          userNFTs.push({
            tokenId: tokenIdStr,
            name: `Catch Mew If You Can #${tokenIdStr.padStart(3, '0')}`,
            image: getMewToyImageUrl(undefined),
          });
        }
      }

      logger.contract(`Mew NFTs loaded`, {
        totalFound: userNFTs.length,
        expected: nftCount,
      });

      return userNFTs;
    } catch (err) {
      logger.contractError("Failed to load Mew NFTs", err as Error, {
        address,
        contractAddress: process.env.REACT_APP_MEW_CONTRACT_ADDRESS,
      });
      return [];
    }
  };

  // Load NFTs from Chimpers, Ponke, and Mew
  const loadUserNFTs = async () => {
    // We need eoaAddress for Chimpers and address for Ponke/Mew
    if (!eoaAddress && !address) return;

    logger.contract(`Starting to load user Digital Collectibles from all chains`, {
      eoaAddress,
      address,
    });
    setIsLoading(true);
    setError(null);

    try {
      // Load NFTs from all chains in parallel
      const [chimpersNFTs, ponkeNFTs, mewNFTs] = await Promise.all([
        loadChimpersNFTs(),
        loadPonkeNFTs(),
        loadMewNFTs(),
      ]);

      // Combine all results
      const allNFTs = [...chimpersNFTs, ...ponkeNFTs, ...mewNFTs];

      logger.contract(`All Digital Collectibles loaded`, {
        chimpers: chimpersNFTs.length,
        ponke: ponkeNFTs.length,
        mew: mewNFTs.length,
        total: allNFTs.length,
      });
console.log('allNFTs', allNFTs);
      setNfts(allNFTs);
    } catch (err) {
      logger.contractError(
        "Failed to load user Digital Collectibles",
        err as Error,
        {
          eoaAddress,
          address,
        }
      );
      setError("Failed to load Digital Collectibles");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRevealRewards = async () => {
    if (!eoaAddress || !eoaAccount) return;

    logger.api(`Starting reveal rewards process`, {
      address: eoaAddress,
      nftCount: nfts.length,
    });
    setIsSigning(true);
    setError(null);

    try {
      const message = "Validate ownership of 223 Digital Collectibles";
      logger.api(`Requesting signature for message`, {
        message,
        address: eoaAddress,
      });

      const signature = await eoaAccount.signMessage({ message });
      logger.api(`Signature received`, {
        signatureLength: signature.length,
        address: eoaAddress,
      });

      // Send to backend to get secret links
      logger.api(`Sending request to backend`, {
        endpoint: "/api/claim-additional",
        address: eoaAddress,
        signatureLength: signature.length,
      });

      const { data } = await sendRequest<{ links: Record<string, string> }>({
        endpoint: "/api/claim-additional",
        method: "POST",
        headers: { "Content-Type": "application/json" },
        payload: { address: eoaAddress, signature },
        responseType: "json",
      });

      const linksObject = (data as any).links || {};

      logger.api(`Secret links received`, {
        linkCount: Object.keys(linksObject).length,
        tokenIds: Object.keys(linksObject),
        address: eoaAddress,
      });

      setRevealedLinks(linksObject);

      navigate("/chimpers-reward", {
        state: {
          revealedLinks: linksObject,
          address: eoaAddress,
        },
      });
    } catch (err) {
      logger.apiError("Failed to reveal rewards", err as Error, {
        address: eoaAddress,
        nftCount: nfts.length,
      });
      setError(err instanceof Error ? err.message : "Failed to reveal rewards");
    } finally {
      setIsSigning(false);
    }
  };

  if (!address && !isLoading) {
    return (
      <div className="flex justify-center items-center bg-gray-50 min-h-[calc(100dvh-149.5px)] p-6">
        <div className="mx-auto max-w-md text-center">
          <div className="p-8 bg-white rounded-lg shadow-md">
            <img
              src="/images/223-logo.png"
              alt="223 Logo"
              className="mx-auto mb-4 max-w-[100px]"
            />

            <h1 className="mb-4 text-2xl font-bold text-gray-900">
              Connect Your Wallet
            </h1>

            <p className="mb-8 text-gray-600">
              Connect your wallet to view your 223 Digital Collectibles and
              reveal additional rewards.
            </p>

            {error && (
              <div className="px-4 py-3 mb-4 text-red-700 bg-red-100 rounded border border-red-400">
                {error}
              </div>
            )}

            <div className="flex justify-center">
              <ThirdwebWalletConnect showDropdown={false} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="p-6 space-y-5">
        <div className="border border-black rounded-[14px] p-3.5 w-full">
          <div className="flex justify-between items-center">
            <h6 className="font-bold">My Digital Collectibles</h6>

            <div className="flex items-center gap-1.5">
              <svg
                width="16"
                height="17"
                viewBox="0 0 16 17"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11.3333 9.83333C11.5101 9.83333 11.6797 9.7631 11.8047 9.63807C11.9298 9.51305 12 9.34348 12 9.16667C12 8.98986 11.9298 8.82029 11.8047 8.69526C11.6797 8.57024 11.5101 8.5 11.3333 8.5C11.1565 8.5 10.987 8.57024 10.8619 8.69526C10.7369 8.82029 10.6667 8.98986 10.6667 9.16667C10.6667 9.34348 10.7369 9.51305 10.8619 9.63807C10.987 9.7631 11.1565 9.83333 11.3333 9.83333ZM11.3333 12.5C11.5101 12.5 11.6797 12.4298 11.8047 12.3047C11.9298 12.1797 12 12.0101 12 11.8333C12 11.6565 11.9298 11.487 11.8047 11.3619C11.6797 11.2369 11.5101 11.1667 11.3333 11.1667C11.1565 11.1667 10.987 11.2369 10.8619 11.3619C10.7369 11.487 10.6667 11.6565 10.6667 11.8333C10.6667 12.0101 10.7369 12.1797 10.8619 12.3047C10.987 12.4298 11.1565 12.5 11.3333 12.5ZM8.66667 9.16667C8.66667 9.34348 8.59643 9.51305 8.4714 9.63807C8.34638 9.7631 8.17681 9.83333 8 9.83333C7.82319 9.83333 7.65362 9.7631 7.5286 9.63807C7.40357 9.51305 7.33333 9.34348 7.33333 9.16667C7.33333 8.98986 7.40357 8.82029 7.5286 8.69526C7.65362 8.57024 7.82319 8.5 8 8.5C8.17681 8.5 8.34638 8.57024 8.4714 8.69526C8.59643 8.82029 8.66667 8.98986 8.66667 9.16667ZM8.66667 11.8333C8.66667 12.0101 8.59643 12.1797 8.4714 12.3047C8.34638 12.4298 8.17681 12.5 8 12.5C7.82319 12.5 7.65362 12.4298 7.5286 12.3047C7.40357 12.1797 7.33333 12.0101 7.33333 11.8333C7.33333 11.6565 7.40357 11.487 7.5286 11.3619C7.65362 11.2369 7.82319 11.1667 8 11.1667C8.17681 11.1667 8.34638 11.2369 8.4714 11.3619C8.59643 11.487 8.66667 11.6565 8.66667 11.8333ZM4.66667 9.83333C4.84348 9.83333 5.01305 9.7631 5.13807 9.63807C5.2631 9.51305 5.33333 9.34348 5.33333 9.16667C5.33333 8.98986 5.2631 8.82029 5.13807 8.69526C5.01305 8.57024 4.84348 8.5 4.66667 8.5C4.48986 8.5 4.32029 8.57024 4.19526 8.69526C4.07024 8.82029 4 8.98986 4 9.16667C4 9.34348 4.07024 9.51305 4.19526 9.63807C4.32029 9.7631 4.48986 9.83333 4.66667 9.83333ZM4.66667 12.5C4.84348 12.5 5.01305 12.4298 5.13807 12.3047C5.2631 12.1797 5.33333 12.0101 5.33333 11.8333C5.33333 11.6565 5.2631 11.487 5.13807 11.3619C5.01305 11.2369 4.84348 11.1667 4.66667 11.1667C4.48986 11.1667 4.32029 11.2369 4.19526 11.3619C4.07024 11.487 4 11.6565 4 11.8333C4 12.0101 4.07024 12.1797 4.19526 12.3047C4.32029 12.4298 4.48986 12.5 4.66667 12.5Z"
                  fill="black"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M4.66665 1.66699C4.79926 1.66699 4.92644 1.71967 5.0202 1.81344C5.11397 1.90721 5.16665 2.03438 5.16665 2.16699V2.67566C5.60798 2.66699 6.09398 2.66699 6.62865 2.66699H9.37065C9.90598 2.66699 10.392 2.66699 10.8333 2.67566V2.16699C10.8333 2.03438 10.886 1.90721 10.9798 1.81344C11.0735 1.71967 11.2007 1.66699 11.3333 1.66699C11.4659 1.66699 11.5931 1.71967 11.6869 1.81344C11.7806 1.90721 11.8333 2.03438 11.8333 2.16699V2.71833C12.0067 2.73166 12.1709 2.74855 12.326 2.76899C13.1073 2.87433 13.74 3.09566 14.2393 3.59433C14.738 4.09366 14.9593 4.72633 15.0647 5.50766C15.1667 6.26766 15.1667 7.23766 15.1667 8.46299V9.87099C15.1667 11.0963 15.1667 12.067 15.0647 12.8263C14.9593 13.6077 14.738 14.2403 14.2393 14.7397C13.74 15.2383 13.1073 15.4597 12.326 15.565C11.566 15.667 10.596 15.667 9.37065 15.667H6.62998C5.40465 15.667 4.43398 15.667 3.67465 15.565C2.89332 15.4597 2.26065 15.2383 1.76132 14.7397C1.26265 14.2403 1.04132 13.6077 0.935984 12.8263C0.833984 12.0663 0.833984 11.0963 0.833984 9.87099V8.46299C0.833984 7.23766 0.833984 6.26699 0.935984 5.50766C1.04132 4.72633 1.26265 4.09366 1.76132 3.59433C2.26065 3.09566 2.89332 2.87433 3.67465 2.76899C3.83021 2.74855 3.99443 2.73166 4.16732 2.71833V2.16699C4.16732 2.0345 4.2199 1.90742 4.31353 1.81367C4.40715 1.71993 4.53416 1.66717 4.66665 1.66699ZM3.80665 3.76033C3.13665 3.85033 2.74998 4.01966 2.46798 4.30166C2.18598 4.58366 2.01665 4.97033 1.92665 5.64033C1.91154 5.75366 1.89865 5.87344 1.88798 5.99966H14.112C14.1013 5.87344 14.0884 5.75344 14.0733 5.63966C13.9833 4.96966 13.814 4.58299 13.532 4.30099C13.25 4.01899 12.8633 3.84966 12.1927 3.75966C11.508 3.66766 10.6047 3.66633 9.33332 3.66633H6.66665C5.39532 3.66633 4.49265 3.66833 3.80665 3.76033ZM1.83332 8.50033C1.83332 7.93099 1.83332 7.43566 1.84198 7.00033H14.158C14.1667 7.43566 14.1667 7.93099 14.1667 8.50033V9.83366C14.1667 11.105 14.1653 12.0083 14.0733 12.6937C13.9833 13.3637 13.814 13.7503 13.532 14.0323C13.25 14.3143 12.8633 14.4837 12.1927 14.5737C11.508 14.6657 10.6047 14.667 9.33332 14.667H6.66665C5.39532 14.667 4.49265 14.6657 3.80665 14.5737C3.13665 14.4837 2.74998 14.3143 2.46798 14.0323C2.18598 13.7503 2.01665 13.3637 1.92665 12.693C1.83465 12.0083 1.83332 11.105 1.83332 9.83366V8.50033Z"
                  fill="black"
                />
              </svg>

              <span className="text-xs">
                {new Date().toLocaleDateString("en-US", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </span>
            </div>
          </div>

          {isLoading ? (
            <div className="flex justify-center items-center py-8">
              <div className="inline-block w-8 h-8 rounded-full border-b-2 border-gray-900 animate-spin"></div>
              <p className="ml-2 text-gray-600">
                Loading your Digital Collectibles...
              </p>
            </div>
          ) : nfts.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-gray-600">
                You don't own any 223 Digital Collectibles yet.
              </p>
            </div>
          ) : (
            <div className="mt-4">
              {nfts.length === 1 && (
                <div className="relative rounded-[14px] overflow-hidden aspect-[16/9]">
                  <img
                    src={nfts[0].name?.includes('Ponke') 
                      ? `/images/ponke/1.png` 
                      : `/images/toys/${(nfts[0].toyType ?? 0) + 1}.png`}
                    alt={nfts[0].name}
                    className="object-cover w-full h-full"
                  />

                  <div className="absolute inset-x-2.5 bottom-2.5 px-2 py-1 border border-white backdrop-blur-md rounded-lg">
                    <h3 className="text-lg font-bold text-white">
                      {nfts[0].name}
                    </h3>

                    <p className="text-[10px] text-white">Claimed</p>
                  </div>
                </div>
              )}

              {nfts.length === 2 && (
                <div className="grid grid-cols-2 gap-3">
                  {nfts.map((nft) => (
                    <div
                      key={nft.tokenId}
                      className="relative rounded-[14px] overflow-hidden aspect-square"
                    >
                      <img
                        src={nft.name?.includes('Ponke') 
                          ? `/images/ponke/1.png` 
                          : `/images/toys/${(nft.toyType ?? 0) + 1}.png`}
                        alt={nft.name}
                        className="object-cover w-full h-full"
                      />

                      <div className="absolute inset-x-2.5 bottom-2.5 p-1 border border-white backdrop-blur-md rounded-lg">
                        <h3 className="text-sm font-bold text-white">
                          {nft.name}
                        </h3>

                        {!nft.name?.includes('Ponke') && (
                          <div className="flex gap-1 items-center text-[8px] text-white">
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 12 12"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M6 1C8.7615 1 11 3.2385 11 6C11 8.7615 8.7615 11 6 11C3.2385 11 1 8.7615 1 6C1 3.2385 3.2385 1 6 1ZM6.741 8.47C6.24784 8.51074 5.75216 8.51074 5.259 8.47C5.30567 8.66867 5.35733 8.85233 5.414 9.021C5.5395 9.3985 5.679 9.6675 5.809 9.832C5.8725 9.913 5.924 9.956 5.959 9.977L5.9845 9.991L5.9995 9.995L6.0155 9.991L6.041 9.9775C6.09908 9.9383 6.14985 9.88922 6.191 9.8325C6.321 9.6675 6.4605 9.3985 6.586 9.021C6.64267 8.85233 6.69433 8.66867 6.741 8.47ZM2.4035 7.7525C2.85773 8.68194 3.65317 9.40019 4.624 9.7575C4.5653 9.61972 4.51226 9.47961 4.465 9.3375C4.35618 9.00472 4.26933 8.66516 4.205 8.321C3.58622 8.19522 2.98239 8.00467 2.4035 7.7525ZM9.597 7.7525C9.0245 8.0025 8.4215 8.194 7.7945 8.321C7.73019 8.66533 7.64334 9.00506 7.5345 9.338C7.4865 9.48367 7.4335 9.62333 7.3755 9.757C8.3467 9.39991 9.14254 8.68163 9.597 7.752V7.7525ZM6.988 5.4355C6.33222 5.52265 5.66778 5.52265 5.012 5.4355C4.98343 6.10715 5.007 6.78 5.0825 7.448C5.69219 7.51751 6.30781 7.51751 6.9175 7.448C6.973 6.96734 7.00055 6.48386 7 6C7 5.808 6.996 5.61967 6.988 5.435V5.4355ZM2.284 4.516C2.03999 5.12748 1.95068 5.78973 2.024 6.444C2.66107 6.80885 3.34532 7.08426 4.0575 7.2625C3.99622 6.58878 3.98386 5.91151 4.0205 5.236C3.41376 5.06978 2.83035 4.82788 2.284 4.516ZM9.716 4.516C9.16965 4.82788 8.58624 5.06978 7.9795 5.236C8.01614 5.91151 8.00378 6.58878 7.9425 7.2625C8.65451 7.08422 9.33859 6.8088 9.9755 6.444C10.0487 5.78989 9.95943 5.12782 9.7155 4.5165L9.716 4.516ZM6.008 2.006L6.0025 2.005L5.9925 2.0065L5.9735 2.014C5.9093 2.05429 5.85331 2.10637 5.8085 2.1675C5.6785 2.3325 5.5395 2.6015 5.4135 2.9785C5.2785 3.3825 5.1695 3.8785 5.097 4.438C5.69607 4.52204 6.30393 4.52204 6.903 4.438C6.8305 3.878 6.721 3.3825 6.5865 2.9785C6.4605 2.6015 6.3215 2.332 6.1915 2.1675C6.15035 2.11078 6.09958 2.0617 6.0415 2.0225L6.008 2.006ZM4.624 2.243C3.88093 2.51539 3.23537 3.00209 2.769 3.6415C3.1905 3.8835 3.642 4.0795 4.1165 4.223C4.1965 3.641 4.315 3.1115 4.465 2.662C4.51333 2.516 4.56633 2.37633 4.624 2.243ZM7.376 2.243C7.43333 2.37633 7.48633 2.516 7.535 2.662C7.685 3.112 7.8035 3.641 7.8835 4.223C8.35318 4.08066 8.80523 3.88558 9.231 3.6415C8.76463 3.00209 8.11907 2.51539 7.376 2.243Z"
                                fill="white"
                              />
                            </svg>
                            <span>Toy Type {nft.toyType}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {nfts.length === 3 && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="relative rounded-[14px] overflow-hidden aspect-auto">
                    <img
                      src={nfts[0].name?.includes('Ponke') 
                        ? `/images/ponke/1.png` 
                        : `/images/toys/${(nfts[0].toyType ?? 0) + 1}.png`}
                      alt={nfts[0].name}
                      className="object-cover w-full h-full"
                    />
                    <div className="absolute inset-x-2.5 bottom-2.5 px-2 py-1 border border-white backdrop-blur-md rounded-lg">
                      <h3 className="text-lg font-bold text-white">
                        {nfts[0].name}
                      </h3>
                      <p className="text-[10px] text-white">Claimed</p>
                    </div>
                  </div>

                  <div className="flex flex-col gap-3">
                    {nfts.slice(1, 3).map((nft) => (
                      <div
                        key={nft.tokenId}
                        className="relative rounded-[14px] overflow-hidden aspect-[4/3]"
                      >
                        <img
                          src={nft.name?.includes('Ponke') 
                            ? `/images/ponke/1.png` 
                            : `/images/toys/${(nft.toyType ?? 0) + 1}.png`}
                          alt={nft.name}
                          className="object-cover w-full h-full"
                        />
                        <div className="absolute inset-x-2.5 bottom-2.5 p-1 border border-white backdrop-blur-md rounded-lg">
                          <h3 className="text-sm font-bold text-white">
                            {nft.name}
                          </h3>
                          {!nft.name?.includes('Ponke') && (
                            <div className="flex gap-1 items-center text-[8px] text-white">
                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 12 12"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  fillRule="evenodd"
                                  clipRule="evenodd"
                                  d="M6 1C8.7615 1 11 3.2385 11 6C11 8.7615 8.7615 11 6 11C3.2385 11 1 8.7615 1 6C1 3.2385 3.2385 1 6 1ZM6.741 8.47C6.24784 8.51074 5.75216 8.51074 5.259 8.47C5.30567 8.66867 5.35733 8.85233 5.414 9.021C5.5395 9.3985 5.679 9.6675 5.809 9.832C5.8725 9.913 5.924 9.956 5.959 9.977L5.9845 9.991L5.9995 9.995L6.0155 9.991L6.041 9.9775C6.09908 9.9383 6.14985 9.88922 6.191 9.8325C6.321 9.6675 6.4605 9.3985 6.586 9.021C6.64267 8.85233 6.69433 8.66867 6.741 8.47ZM2.4035 7.7525C2.85773 8.68194 3.65317 9.40019 4.624 9.7575C4.5653 9.61972 4.51226 9.47961 4.465 9.3375C4.35618 9.00472 4.26933 8.66516 4.205 8.321C3.58622 8.19522 2.98239 8.00467 2.4035 7.7525ZM9.597 7.7525C9.0245 8.0025 8.4215 8.194 7.7945 8.321C7.73019 8.66533 7.64334 9.00506 7.5345 9.338C7.4865 9.48367 7.4335 9.62333 7.3755 9.757C8.3467 9.39991 9.14254 8.68163 9.597 7.752V7.7525ZM6.988 5.4355C6.33222 5.52265 5.66778 5.52265 5.012 5.4355C4.98343 6.10715 5.007 6.78 5.0825 7.448C5.69219 7.51751 6.30781 7.51751 6.9175 7.448C6.973 6.96734 7.00055 6.48386 7 6C7 5.808 6.996 5.61967 6.988 5.435V5.4355ZM2.284 4.516C2.03999 5.12748 1.95068 5.78973 2.024 6.444C2.66107 6.80885 3.34532 7.08426 4.0575 7.2625C3.99622 6.58878 3.98386 5.91151 4.0205 5.236C3.41376 5.06978 2.83035 4.82788 2.284 4.516ZM9.716 4.516C9.16965 4.82788 8.58624 5.06978 7.9795 5.236C8.01614 5.91151 8.00378 6.58878 7.9425 7.2625C8.65451 7.08422 9.33859 6.8088 9.9755 6.444C10.0487 5.78989 9.95943 5.12782 9.7155 4.5165L9.716 4.516ZM6.008 2.006L6.0025 2.005L5.9925 2.0065L5.9735 2.014C5.9093 2.05429 5.85331 2.10637 5.8085 2.1675C5.6785 2.3325 5.5395 2.6015 5.4135 2.9785C5.2785 3.3825 5.1695 3.8785 5.097 4.438C5.69607 4.52204 6.30393 4.52204 6.903 4.438C6.8305 3.878 6.721 3.3825 6.5865 2.9785C6.4605 2.6015 6.3215 2.332 6.1915 2.1675C6.15035 2.11078 6.09958 2.0617 6.0415 2.0225L6.008 2.006ZM4.624 2.243C3.88093 2.51539 3.23537 3.00209 2.769 3.6415C3.1905 3.8835 3.642 4.0795 4.1165 4.223C4.1965 3.641 4.315 3.1115 4.465 2.662C4.51333 2.516 4.56633 2.37633 4.624 2.243ZM7.376 2.243C7.43333 2.37633 7.48633 2.516 7.535 2.662C7.685 3.112 7.8035 3.641 7.8835 4.223C8.35318 4.08066 8.80523 3.88558 9.231 3.6415C8.76463 3.00209 8.11907 2.51539 7.376 2.243Z"
                                  fill="white"
                                />
                              </svg>
                              <span>Toy Type {nft.toyType}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {nfts.length >= 4 && (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    {(showAllNFTs ? nfts : nfts.slice(0, 4)).map((nft) => (
                      <div
                        key={nft.tokenId}
                        className="relative rounded-[14px] overflow-hidden aspect-square"
                      >
                        <img
                          src={nft.name?.includes('Ponke') 
                            ? `/images/ponke/1.png` 
                            : `/images/toys/${(nft.toyType ?? 0) + 1}.png`}
                          alt={nft.name}
                          className="object-cover w-full h-full"
                        />
                        <div className="absolute inset-x-2.5 bottom-2.5 p-1 border border-white backdrop-blur-md rounded-lg">
                          <h3 className="text-sm font-bold text-white">
                            {nft.name}
                          </h3>
                          {!nft.name?.includes('Ponke') && (
                            <div className="flex gap-1 items-center text-[8px] text-white">
                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 12 12"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  fillRule="evenodd"
                                  clipRule="evenodd"
                                  d="M6 1C8.7615 1 11 3.2385 11 6C11 8.7615 8.7615 11 6 11C3.2385 11 1 8.7615 1 6C1 3.2385 3.2385 1 6 1ZM6.741 8.47C6.24784 8.51074 5.75216 8.51074 5.259 8.47C5.30567 8.66867 5.35733 8.85233 5.414 9.021C5.5395 9.3985 5.679 9.6675 5.809 9.832C5.8725 9.913 5.924 9.956 5.959 9.977L5.9845 9.991L5.9995 9.995L6.0155 9.991L6.041 9.9775C6.09908 9.9383 6.14985 9.88922 6.191 9.8325C6.321 9.6675 6.4605 9.3985 6.586 9.021C6.64267 8.85233 6.69433 8.66867 6.741 8.47ZM2.4035 7.7525C2.85773 8.68194 3.65317 9.40019 4.624 9.7575C4.5653 9.61972 4.51226 9.47961 4.465 9.3375C4.35618 9.00472 4.26933 8.66516 4.205 8.321C3.58622 8.19522 2.98239 8.00467 2.4035 7.7525ZM9.597 7.7525C9.0245 8.0025 8.4215 8.194 7.7945 8.321C7.73019 8.66533 7.64334 9.00506 7.5345 9.338C7.4865 9.48367 7.4335 9.62333 7.3755 9.757C8.3467 9.39991 9.14254 8.68163 9.597 7.752V7.7525ZM6.988 5.4355C6.33222 5.52265 5.66778 5.52265 5.012 5.4355C4.98343 6.10715 5.007 6.78 5.0825 7.448C5.69219 7.51751 6.30781 7.51751 6.9175 7.448C6.973 6.96734 7.00055 6.48386 7 6C7 5.808 6.996 5.61967 6.988 5.435V5.4355ZM2.284 4.516C2.03999 5.12748 1.95068 5.78973 2.024 6.444C2.66107 6.80885 3.34532 7.08426 4.0575 7.2625C3.99622 6.58878 3.98386 5.91151 4.0205 5.236C3.41376 5.06978 2.83035 4.82788 2.284 4.516ZM9.716 4.516C9.16965 4.82788 8.58624 5.06978 7.9795 5.236C8.01614 5.91151 8.00378 6.58878 7.9425 7.2625C8.65451 7.08422 9.33859 6.8088 9.9755 6.444C10.0487 5.78989 9.95943 5.12782 9.7155 4.5165L9.716 4.516ZM6.008 2.006L6.0025 2.005L5.9925 2.0065L5.9735 2.014C5.9093 2.05429 5.85331 2.10637 5.8085 2.1675C5.6785 2.3325 5.5395 2.6015 5.4135 2.9785C5.2785 3.3825 5.1695 3.8785 5.097 4.438C5.69607 4.52204 6.30393 4.52204 6.903 4.438C6.8305 3.878 6.721 3.3825 6.5865 2.9785C6.4605 2.6015 6.3215 2.332 6.1915 2.1675C6.15035 2.11078 6.09958 2.0617 6.0415 2.0225L6.008 2.006ZM4.624 2.243C3.88093 2.51539 3.23537 3.00209 2.769 3.6415C3.1905 3.8835 3.642 4.0795 4.1165 4.223C4.1965 3.641 4.315 3.1115 4.465 2.662C4.51333 2.516 4.56633 2.37633 4.624 2.243ZM7.376 2.243C7.43333 2.37633 7.48633 2.516 7.535 2.662C7.685 3.112 7.8035 3.641 7.8835 4.223C8.35318 4.08066 8.80523 3.88558 9.231 3.6415C8.76463 3.00209 8.11907 2.51539 7.376 2.243Z"
                                  fill="white"
                                />
                              </svg>
                              <span>Toy Type {nft.toyType}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {nfts.length > 4 && (
                    <div className="flex justify-center">
                      <button
                        onClick={() => setShowAllNFTs(!showAllNFTs)}
                        className="px-6 py-2 text-sm font-semibold text-black bg-white border-2 border-black rounded-[50px] hover:bg-black hover:text-white transition-colors"
                      >
                        {showAllNFTs
                          ? `Show Less`
                          : `Show All (${nfts.length})`}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {error && (
          <div className="px-4 py-3 mb-4 text-red-700 bg-red-100 rounded border border-red-400">
            {error}
          </div>
        )}

        <div className="bg-gradient-to-br from-[#2C3E50] to-[#4CA1AF] p-3 rounded-[14px] min-h-[175px] relative flex items-center">
          <img
            src="/images/chimpers/chimpy.png"
            alt="chimpers"
            className="absolute bottom-0 right-3.5 max-w-[132px]"
          />

          <div className="relative z-10 space-y-7">
            <img
              src="/images/chimpers/chimpers-logo-lg.png"
              alt="chimpers-logo"
              className="max-w-[200px]"
            />

            <button
              className="bg-gradient-to-br from-[#2C3E50] to-[#4CA1AF] text-white font-bold rounded-lg text-sm px-3 h-8 flex items-center justify-center gap-1.5 min-w-[151px]"
              onClick={() => navigate("/rewards/chimpers")}
            >
              <span>View Chimpers</span>

              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8.98004 5.68663C8.89172 5.59184 8.84364 5.46648 8.84592 5.33694C8.84821 5.20741 8.90068 5.08382 8.99229 4.99221C9.0839 4.9006 9.20749 4.84813 9.33703 4.84584C9.46656 4.84356 9.59192 4.89164 9.68671 4.97996L12.3534 7.64663C12.447 7.74038 12.4996 7.86746 12.4996 7.99996C12.4996 8.13246 12.447 8.25954 12.3534 8.35329L9.68671 11.02C9.64093 11.0691 9.58573 11.1085 9.5244 11.1358C9.46307 11.1631 9.39686 11.1778 9.32972 11.179C9.26259 11.1802 9.1959 11.1679 9.13364 11.1427C9.07138 11.1176 9.01483 11.0801 8.96735 11.0327C8.91987 10.9852 8.88244 10.9286 8.85729 10.8664C8.83214 10.8041 8.81979 10.7374 8.82098 10.6703C8.82216 10.6031 8.83686 10.5369 8.86419 10.4756C8.89151 10.4143 8.93092 10.3591 8.98004 10.3133L10.7934 8.49996H4.33337C4.20077 8.49996 4.07359 8.44728 3.97982 8.35351C3.88605 8.25974 3.83337 8.13257 3.83337 7.99996C3.83337 7.86735 3.88605 7.74017 3.97982 7.64641C4.07359 7.55264 4.20077 7.49996 4.33337 7.49996H10.7934L8.98004 5.68663Z"
                  fill="white"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="bg-gradient-to-br from-[#876836] to-[#C7A570] p-3 rounded-[14px] min-h-[175px] relative flex items-center">
          <img
            src="/images/ponke/ponke-on-tree.png"
            alt="ponke-on-tree"
            className="absolute bottom-0 right-0 max-w-[225px]"
          />

          <div className="relative z-10 space-y-3">
            <img
              src="/images/ponke/ponke-logo-lg.png"
              alt="ponke-logo"
              className="max-w-[190px]"
            />

            <button
              className="bg-gradient-to-br from-[#876836] to-[#C7A570] text-white font-bold rounded-lg text-sm px-3 h-8 flex items-center justify-center gap-1.5 min-w-[151px]"
              onClick={() => navigate("/rewards/ponke")}
            >
              <span>View Ponke</span>

              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8.98004 5.68663C8.89172 5.59184 8.84364 5.46648 8.84592 5.33694C8.84821 5.20741 8.90068 5.08382 8.99229 4.99221C9.0839 4.9006 9.20749 4.84813 9.33703 4.84584C9.46656 4.84356 9.59192 4.89164 9.68671 4.97996L12.3534 7.64663C12.447 7.74038 12.4996 7.86746 12.4996 7.99996C12.4996 8.13246 12.447 8.25954 12.3534 8.35329L9.68671 11.02C9.64093 11.0691 9.58573 11.1085 9.5244 11.1358C9.46307 11.1631 9.39686 11.1778 9.32972 11.179C9.26259 11.1802 9.1959 11.1679 9.13364 11.1427C9.07138 11.1176 9.01483 11.0801 8.96735 11.0327C8.91987 10.9852 8.88244 10.9286 8.85729 10.8664C8.83214 10.8041 8.81979 10.7374 8.82098 10.6703C8.82216 10.6031 8.83686 10.5369 8.86419 10.4756C8.89151 10.4143 8.93092 10.3591 8.98004 10.3133L10.7934 8.49996H4.33337C4.20077 8.49996 4.07359 8.44728 3.97982 8.35351C3.88605 8.25974 3.83337 8.13257 3.83337 7.99996C3.83337 7.86735 3.88605 7.74017 3.97982 7.64641C4.07359 7.55264 4.20077 7.49996 4.33337 7.49996H10.7934L8.98004 5.68663Z"
                  fill="white"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="bg-gradient-to-br from-[#5ACCF1] to-[#1173BB] p-3 rounded-[14px] min-h-[175px] relative flex items-center">
          <img
            src="/images/catgame/cat-handsup.png"
            alt="catgame handsup"
            className="absolute bottom-0 right-0 max-w-[175px]"
          />

          <div className="relative z-10 space-y-3">
            <img
              src="/images/catgame/catgame.png"
              alt="catgame-logo"
              className="max-w-[155px]"
            />

            <button
              className="bg-gradient-to-br from-[#1173BB] to-[#5ACCF1] text-white font-bold rounded-lg text-sm px-3 h-8 flex items-center justify-center gap-1.5 min-w-[151px]"
              onClick={() => navigate("/rewards/catgame")}
            >
              <span>View Catgame</span>

              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8.98004 5.68663C8.89172 5.59184 8.84364 5.46648 8.84592 5.33694C8.84821 5.20741 8.90068 5.08382 8.99229 4.99221C9.0839 4.9006 9.20749 4.84813 9.33703 4.84584C9.46656 4.84356 9.59192 4.89164 9.68671 4.97996L12.3534 7.64663C12.447 7.74038 12.4996 7.86746 12.4996 7.99996C12.4996 8.13246 12.447 8.25954 12.3534 8.35329L9.68671 11.02C9.64093 11.0691 9.58573 11.1085 9.5244 11.1358C9.46307 11.1631 9.39686 11.1778 9.32972 11.179C9.26259 11.1802 9.1959 11.1679 9.13364 11.1427C9.07138 11.1176 9.01483 11.0801 8.96735 11.0327C8.91987 10.9852 8.88244 10.9286 8.85729 10.8664C8.83214 10.8041 8.81979 10.7374 8.82098 10.6703C8.82216 10.6031 8.83686 10.5369 8.86419 10.4756C8.89151 10.4143 8.93092 10.3591 8.98004 10.3133L10.7934 8.49996H4.33337C4.20077 8.49996 4.07359 8.44728 3.97982 8.35351C3.88605 8.25974 3.83337 8.13257 3.83337 7.99996C3.83337 7.86735 3.88605 7.74017 3.97982 7.64641C4.07359 7.55264 4.20077 7.49996 4.33337 7.49996H10.7934L8.98004 5.68663Z"
                  fill="white"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="bg-gradient-to-br from-[#FD90E9] to-[#852A93] p-3 rounded-[14px] min-h-[175px] relative flex items-center">
          <img
            src="/images/mew/mew-car.png"
            alt="catgame handsup"
            className="absolute bottom-0 right-0 max-w-[141px]"
          />

          <div className="relative z-10 space-y-3">
            <img
              src="/images/mew/mew.png"
              alt="catgame-logo"
              className="max-w-[200px]"
            />

            <button
              className="bg-gradient-to-br from-[#852A93] to-[#FD90E9] text-white font-bold rounded-lg text-sm px-3 h-8 flex items-center justify-center gap-1.5 min-w-[151px]"
              onClick={() => navigate("/rewards/mew")}
            >
              <span>View Mew</span>

              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8.98004 5.68663C8.89172 5.59184 8.84364 5.46648 8.84592 5.33694C8.84821 5.20741 8.90068 5.08382 8.99229 4.99221C9.0839 4.9006 9.20749 4.84813 9.33703 4.84584C9.46656 4.84356 9.59192 4.89164 9.68671 4.97996L12.3534 7.64663C12.447 7.74038 12.4996 7.86746 12.4996 7.99996C12.4996 8.13246 12.447 8.25954 12.3534 8.35329L9.68671 11.02C9.64093 11.0691 9.58573 11.1085 9.5244 11.1358C9.46307 11.1631 9.39686 11.1778 9.32972 11.179C9.26259 11.1802 9.1959 11.1679 9.13364 11.1427C9.07138 11.1176 9.01483 11.0801 8.96735 11.0327C8.91987 10.9852 8.88244 10.9286 8.85729 10.8664C8.83214 10.8041 8.81979 10.7374 8.82098 10.6703C8.82216 10.6031 8.83686 10.5369 8.86419 10.4756C8.89151 10.4143 8.93092 10.3591 8.98004 10.3133L10.7934 8.49996H4.33337C4.20077 8.49996 4.07359 8.44728 3.97982 8.35351C3.88605 8.25974 3.83337 8.13257 3.83337 7.99996C3.83337 7.86735 3.88605 7.74017 3.97982 7.64641C4.07359 7.55264 4.20077 7.49996 4.33337 7.49996H10.7934L8.98004 5.68663Z"
                  fill="white"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
